import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Spinner } from './spinner'

/* Botoes em pilula do design "Donnor PWA". Use `buttonVariants` para estilizar <Link>/<a>. */
export const buttonVariants = cva(
    'inline-flex items-center justify-center gap-2.5 rounded-full font-bold no-underline transition-colors select-none disabled:cursor-not-allowed disabled:opacity-55',
    {
        variants: {
            variant: {
                primary: 'bg-brand text-brand-foreground hover:bg-brand-strong',
                accent: 'bg-accent text-ink hover:brightness-95',
                outline: 'border-[1.5px] border-brand bg-transparent text-brand hover:bg-brand-soft',
                outlineInk: 'border-[1.5px] border-ink bg-surface text-ink hover:bg-app-background',
                outlineDanger: 'border-[1.5px] border-line bg-surface-warm text-danger hover:bg-sand/40',
                ghost: 'bg-transparent text-ink hover:bg-ink/5',
                danger: 'bg-transparent text-danger hover:bg-danger-soft',
            },
            size: {
                lg: 'h-14 px-6 text-base',
                md: 'h-12 px-5 text-[15px]',
                sm: 'h-11 px-4 text-sm',
            },
            block: { true: 'w-full', false: '' },
        },
        defaultVariants: { variant: 'primary', size: 'lg', block: false },
    },
)

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & VariantProps<typeof buttonVariants> & {
    loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    { className, variant, size, block, loading = false, disabled, children, type = 'button', ...props }, ref,
) {
    return (
        <button
            ref={ref}
            type={type}
            className={cn(buttonVariants({ variant, size, block }), className)}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            {...props}
        >
            {loading ? <Spinner className="size-5" /> : null}
            {children}
        </button>
    )
})
